// GENERATED by server/scripts/gen-town-name.mts from server/src/town-name.ts — do not edit; run `npm run town-name -w server`.
/**
 * The town's name (v1 ticket 40): one rule for the server's `PUT /api/town/name` and the naming screen, so a name
 * the screen accepts is one the server keeps. DOM-free and import-free: `npm run town-name -w server` copies it word
 * for word to web/src/town-name.generated.ts (also part of the root build and typecheck).
 *
 * A name is any script, 1–24 graphemes (what a reader counts as characters: an emoji with its skin tone, a Persian
 * letter with its marks), after trimming, collapsing each run of white space (tabs and line breaks included) to one
 * space and NFC. Control characters and the bidi controls are refused, since they would reorder the text around the
 * name; the Persian half-space (ZWNJ, U+200C) and the emoji joiner (ZWJ, U+200D) are letters' business and kept.
 * Every other format character (soft hyphen, BOM, word joiner, zero-width space and the like), the Hangul and
 * halfwidth-Hangul filler letters and the blank braille pattern are refused too, since none of them draws anything a
 * reader can see. At least one letter, number or symbol must remain (an emoji counts as a symbol), so a name cannot
 * be built entirely from joiners, marks, spaces and punctuation; and no single grapheme may carry more than 4
 * combining marks (Persian with harakat and shadda uses at most a couple), so one character cannot carry an
 * unbounded stack of them.
 */

/** The most graphemes a town's name may have. */
export const TOWN_NAME_MAX = 24;

/** The most combining marks one grapheme (one base character, with whatever it composes with under NFC) may carry. */
const MAX_MARKS_PER_GRAPHEME = 4;

/** Anything longer than this, in UTF-16 units, is refused before it is looked at: no name of 24 graphemes needs it. */
const RAW_MAX = 1024;

/** Runs of white space to collapse to one space. `\s` also matches \uFEFF (the BOM): excluded here, so a stray one still hits the format-character check below rather than being swallowed as a space. */
const WHITESPACE_RUN = /(?:(?!\uFEFF)\s)+/gu;

/** Control characters (C0, DEL, C1) and lone surrogates. */
const CONTROL = /[\p{Cc}\p{Cs}]/u;
/** The bidi controls: ALM, LRM, RLM, the embeddings and overrides (U+202A–U+202E) and the isolates (U+2066–U+2069). */
const BIDI_CONTROL = /[\u061C\u200E\u200F\u202A-\u202E\u2066-\u2069]/u;
/** Any format character (soft hyphen, BOM, word joiner, zero-width space…) other than the two that are kept above. */
const FORMAT_CONTROL = /(?!\u200C)(?!\u200D)\p{Cf}/u;
/** Hangul's own filler letters, the halfwidth Hangul filler and the blank braille pattern: none of them is visible. */
const FILLER = /[\u115F\u1160\u3164\uFFA0\u2800]/u;
/** At least one of these must remain once the checks above pass. */
const VISIBLE = /[\p{L}\p{N}\p{S}]/u;
/** A combining mark: nonspacing, spacing-combining or enclosing. */
const MARK_SOURCE = String.raw`\p{M}`;

/** How many graphemes `s` has. */
export function graphemeCount(s: string): number {
  return [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(s)].length;
}

/**
 * The name as it is kept, or null when it cannot be one: not a string, empty once trimmed, over 24 graphemes,
 * holding a control, bidi-control, stray format or filler character, carrying no visible letter, number or symbol,
 * or stacking more than 4 combining marks on one grapheme.
 */
export function normaliseTownName(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length > RAW_MAX) return null;
  const name = raw.replace(WHITESPACE_RUN, ' ').trim().normalize('NFC');
  if (!name || CONTROL.test(name) || BIDI_CONTROL.test(name) || FORMAT_CONTROL.test(name) || FILLER.test(name)) return null;
  if (!VISIBLE.test(name)) return null;
  const graphemes = [...new Intl.Segmenter(undefined, { granularity: 'grapheme' }).segment(name)];
  if (graphemes.length > TOWN_NAME_MAX) return null;
  const markRe = new RegExp(MARK_SOURCE, 'gu');
  for (const { segment } of graphemes) {
    if ((segment.match(markRe)?.length ?? 0) > MAX_MARKS_PER_GRAPHEME) return null;
  }
  return name;
}
